import { useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../shared/api';
import { ServiceLabels, type ServiceType } from '@census/shared';
import { ServiceForm } from './ServiceForm';
import { Icon } from '../../ui/Icon';

export function ServicesPage() {
  const qc = useQueryClient();
  const gridRef = useRef<AgGridReact | null>(null);
  const [editing, setEditing] = useState<any | null>(null);
  const [showInactive, setShowInactive] = useState(true);

  const { data, isLoading } = useQuery({
    queryKey: ['services'],
    queryFn: () => api.get('/services').then((r) => r.data),
  });

  const services = useMemo(
    () => (showInactive ? data ?? [] : (data ?? []).filter((s: any) => s.active !== false)),
    [data, showInactive],
  );

  const update = useMutation({
    mutationFn: ({ serviceType, patch }: { serviceType: string; patch: any }) =>
      api.patch(`/services/${serviceType}`, patch).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['services'] });
      toast.success('Service updated');
    },
  });

  const cols = useMemo<ColDef[]>(
    () => [
      { headerName: 'Service', field: 'serviceType', pinned: 'left', width: 200 },
      { headerName: 'Name', field: 'displayName', editable: true, width: 200 },
      { headerName: 'Kind', field: 'kind', width: 110 },
      { headerName: 'Duration (min)', field: 'durationMin', editable: true, width: 130 },
      {
        headerName: 'Allowed roles',
        valueGetter: (p) =>
          (p.data?.allowedRoles ?? []).map((r: string) => r.replace(/_/g, ' ')).join(', '),
        flex: 1,
        minWidth: 200,
      },
      {
        headerName: 'Allowed places',
        valueGetter: (p) =>
          (p.data?.allowedPlaces ?? [])
            .map((s: string) => (s === 'HOME_COMMUNITY' ? 'Community' : s))
            .join(', '),
        width: 220,
      },
      {
        headerName: 'Bundles with',
        valueGetter: (p) =>
          (p.data?.bundleHints ?? [])
            .map((s: ServiceType) => ServiceLabels[s] ?? s)
            .join(', '),
        width: 240,
      },
      { headerName: 'Group max', field: 'groupMaxClients', editable: true, width: 110 },
      {
        headerName: 'Status',
        field: 'active',
        width: 100,
        cellRenderer: (p: any) => (
          <span className={p.value !== false ? 'pill-success' : 'pill-muted'}>
            {p.value !== false ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        headerName: 'Actions',
        pinned: 'right',
        width: 200,
        sortable: false,
        cellRenderer: (p: any) => (
          <div className="flex h-full items-center gap-3 text-xs">
            <button
              className="text-brand-700 hover:underline"
              onClick={() => setEditing(p.data)}
            >
              Edit
            </button>
            {p.data.active !== false ? (
              <button
                className="text-rose-600 hover:underline"
                onClick={() => {
                  if (
                    confirm(
                      `Mark ${p.data.serviceType} inactive? Engine skips it for new intakes; existing clients keep it.`,
                    )
                  ) {
                    update.mutate({
                      serviceType: p.data.serviceType,
                      patch: { active: false },
                    });
                  }
                }}
              >
                Deactivate
              </button>
            ) : (
              <button
                className="text-emerald-700 hover:underline"
                onClick={() =>
                  update.mutate({ serviceType: p.data.serviceType, patch: { active: true } })
                }
              >
                Reactivate
              </button>
            )}
          </div>
        ),
      },
    ],
    [update],
  );

  function onCellValueChanged(e: any) {
    if (!e.colDef.editable) return;
    update.mutate({
      serviceType: e.data.serviceType,
      patch: { [e.colDef.field!]: e.newValue },
    });
  }

  return (
    <div className="flex h-full flex-col gap-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">
            Service catalog
          </h1>
          <p className="text-sm text-ink-subtle">
            Fixed list of {(data ?? []).length} services. Click Edit to set durations, roles, places, defaults.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-ink-muted">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show inactive
          </label>
          <button
            className="btn-secondary h-9 whitespace-nowrap"
            onClick={() => gridRef.current?.api.exportDataAsCsv({ fileName: 'services.csv' })}
          >
            <Icon name="print" size={14} /> Export CSV
          </button>
        </div>
      </header>

      <div className="ag-theme-quartz flex-1 overflow-hidden rounded-card shadow-card ring-1 ring-line">
        <AgGridReact
          ref={gridRef}
          rowData={services}
          columnDefs={cols}
          animateRows
          singleClickEdit
          stopEditingWhenCellsLoseFocus
          onCellValueChanged={onCellValueChanged}
          overlayLoadingTemplate={isLoading ? 'Loading…' : ''}
        />
      </div>

      <ServiceForm open={!!editing} onClose={() => setEditing(null)} editing={editing} />
    </div>
  );
}
